import { useCallback } from 'react';
import { cn } from '@/lib/cn';

/**
 * A filled track with a single vertical tick marking a reference point —
 * Insurance Claims wireframe S3's "approved vs running bill" bar: the track
 * is the bill (filled its own percentage, typically the whole thing), and
 * the marker shows where the approved amount sits as a fraction of it. A
 * plain proportional-fill bar can't express "the marker can sit past 100%
 * conceptually, or the fill itself is partial while the marker still needs
 * its own independent position" — this is a track and a marker as two
 * separate values, not one.
 *
 * Both percentages are set via a ref callback (`--fill-pct`/`--marker-pct`
 * CSS custom properties), not an inline `style` prop — `components/ui/**`
 * bans new inline styles; same pattern `features/emergency/ui.tsx`'s
 * `useLevelVar` and the claims split-preview bar already established.
 */
export interface MarkerProgressBarProps {
  /** 0-100. How much of the track is filled — defaults to 100 (the track IS the reference quantity, e.g. the whole bill). */
  fillPct?: number;
  /** 0-100 (not clamped above 100 by this component — a caller showing an over-100 marker should clamp or flag it themselves). Omit to hide the marker. */
  markerPct?: number;
  className?: string;
}

function usePctVars(fillPct: number, markerPct: number | undefined) {
  return useCallback((el: HTMLElement | null) => {
    if (!el) return;
    el.style.setProperty('--fill-pct', `${Math.max(0, Math.min(100, fillPct))}%`);
    el.style.setProperty('--marker-pct', `${Math.max(0, markerPct ?? 0)}%`);
  }, [fillPct, markerPct]);
}

export function MarkerProgressBar({ fillPct = 100, markerPct, className }: MarkerProgressBarProps) {
  const ref = usePctVars(fillPct, markerPct);
  return (
    <div ref={ref} className={cn('relative h-3 overflow-visible rounded-full border border-border bg-muted', className)}>
      <div className="h-full w-[var(--fill-pct)] overflow-hidden rounded-full bg-warn" />
      {markerPct != null && (
        <div className="absolute -top-[3px] h-[18px] w-[2px] translate-x-[-50%] bg-ink left-[var(--marker-pct)]" />
      )}
    </div>
  );
}
